import React, { useState } from 'react'
import { Button, Box, Snackbar, CircularProgress } from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'

import PurchaseOrderForm from './purchaseOrderForm'
import {
  IPurchaseOrder,
  IPurchaseOrderItem,
  PurchaseOrder,
  PurchaseOrderItem,
} from 'interfaces/IPurchaseOrder'
import { isEqual } from 'lodash'

export default function ManagePurchaseOrder({
  purchaseOrder,
  closeDialog,
}: {
  purchaseOrder: PurchaseOrder
  closeDialog?: () => void
}) {
  const [purchaseOrderData, setPurchaseOrderData] =
    useState<IPurchaseOrder>(purchaseOrder)
  const [purchaseOrderItems, setPurchaseOrderItems] = useState<
    IPurchaseOrderItem[]
  >(purchaseOrder.purchaseOrderItems ?? [])
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)

  const [isLoading, setIsLoading] = React.useState<any>(false)
  const [alertMessage, setAlertMessage] = React.useState('')

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const handlePurchaseOrderFieldChange = (value: any, key: string): void => {
    setPurchaseOrderData({ ...purchaseOrderData, [key]: value })
  }
  const handlePurchaseOrderItemFieldChange = (
    value: any,
    key: string,
    changedIdx: number
  ): void => {
    setPurchaseOrderItems((prevState) =>
      prevState.map((purchaseOrderItem, idx) => {
        if (changedIdx === idx)
          return {
            ...purchaseOrderItem,
            [key]: value,
          }
        return purchaseOrderItem
      })
    )
  }

  const handleDeletePurchaseOrderItem = (idxToDelete: number) => {
    setPurchaseOrderItems(
      purchaseOrderItems.filter((purchaseOrderItem, idx) => idx !== idxToDelete)
    )
  }

  const handleAddPurchaseOrderItem = () => {
    setPurchaseOrderItems(purchaseOrderItems.concat(new PurchaseOrderItem({})))
  }

  const handleSavePurchaseOrder = async () => {
    try {
      const mappedItems = purchaseOrderItems.map((purchaseOrderItem) => {
        const totalPrice =
          (purchaseOrderItem.quantityPurchased ?? 0) *
            (purchaseOrderItem.unitPrice ?? 0) -
          (purchaseOrderItem.discount ?? 0)
        return { ...purchaseOrderItem, totalPrice }
      })

      setIsLoading(true)
      const updatedData: Partial<IPurchaseOrder> = {}
      Object.keys(purchaseOrderData).forEach((field) => {
        const purchaseOrderField = field as keyof IPurchaseOrder
        if (
          purchaseOrder[purchaseOrderField] !==
          purchaseOrderData[purchaseOrderField]
        ) {
          const value = purchaseOrderData[purchaseOrderField]
          // @ts-ignore
          updatedData[purchaseOrderField] = value
        }
      })

      let purchaseOrderItemsToUpdate: IPurchaseOrderItem[] = [],
        purchaseOrderItemsToCreate: IPurchaseOrderItem[] = [],
        purchaseOrderItemsToDelete: number[] = []
      const originalPurchaseOrderItems = purchaseOrder.purchaseOrderItems ?? []
      mappedItems.forEach((purchaseOrderItem) => {
        const newPurchaseOrderItem = new PurchaseOrderItem(purchaseOrderItem)
        const matchingOriginalPurchaseOrderItem =
          originalPurchaseOrderItems?.find(
            (purchaseOrderItem) =>
              purchaseOrderItem.purchaseOrderItemId ===
              newPurchaseOrderItem.purchaseOrderItemId
          )
        if (matchingOriginalPurchaseOrderItem) {
          if (
            !isEqual(
              newPurchaseOrderItem,
              new PurchaseOrderItem(matchingOriginalPurchaseOrderItem)
            )
          ) {
            purchaseOrderItemsToUpdate.push(newPurchaseOrderItem)
          } else {
            // do nothing, nothing changed
          }
        } else {
          purchaseOrderItemsToCreate.push({
            ...newPurchaseOrderItem,
            purchaseOrderId: newPurchaseOrderItem.purchaseOrderId,
          })
        }
      })
      originalPurchaseOrderItems.forEach((originalPurchaseOrderItem) => {
        const purchaseOrderItemDeleted = !mappedItems.some(
          (purchaseOrderItem) =>
            originalPurchaseOrderItem.purchaseOrderItemId ===
            purchaseOrderItem.purchaseOrderItemId
        )
        if (
          purchaseOrderItemDeleted &&
          originalPurchaseOrderItem.purchaseOrderItemId
        ) {
          purchaseOrderItemsToDelete.push(
            originalPurchaseOrderItem.purchaseOrderItemId
          )
        }
      })

      setIsLoading(true)
      await API.patch(
        'products',
        `/purchaseOrders/updatePurchaseOrder/${purchaseOrder.purchaseOrderId}`,
        {
          body: {
            ...purchaseOrderData,
            purchaseOrderItemsToCreate,
            purchaseOrderItemsToDelete,
            purchaseOrderItemsToUpdate,
          },
        }
      )

      setAlertMessage('Berhasil mengubah PO.')
      setIsSuccessSnackbarOpen(true)
      closeDialog?.()
    } catch (error) {
      setAlertMessage('Gagal mengubah PO.')
      setIsFailureSnackbarOpen(true)
    }
    setIsLoading(false)
  }

  const isPurchaseOrderValid = () => {
    const requiredPoFields: Array<keyof IPurchaseOrder> = [
      'supplier',
      'poNumber',
      'poDate',
      'type',
    ]
    const requiredPoItemFields: Array<keyof IPurchaseOrderItem> = [
      'itemId',
      'itemName',
      'unitPrice',
      'taxType',
      'quantityPurchased',
    ]
    const isPoValid = requiredPoFields.every((field) =>
      Boolean(purchaseOrderData[field])
    )
    const isAllItemValid = requiredPoItemFields.every((field) =>
      purchaseOrderItems.every((purchaseOrderItem) =>
        Boolean(purchaseOrderItem[field])
      )
    )

    return isPoValid && isAllItemValid
  }

  return (
    <Box>
      <React.Fragment>
        <PurchaseOrderForm
          purchaseOrderData={purchaseOrderData}
          handlePurchaseOrderFieldChange={handlePurchaseOrderFieldChange}
          purchaseOrderItems={purchaseOrderItems}
          handlePurchaseOrderItemFieldChange={
            handlePurchaseOrderItemFieldChange
          }
          handleDeletePurchaseOrderItem={handleDeletePurchaseOrderItem}
          handleAddPurchaseOrderItem={handleAddPurchaseOrderItem}
        />
        <Box mt={2} />
        <Box display="inline">
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!isPurchaseOrderValid()}
            onClick={handleSavePurchaseOrder}
          >
            Save
          </Button>
        </Box>
      </React.Fragment>
      {isLoading && (
        <Box p={3}>
          <CircularProgress />
        </Box>
      )}
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}
